import { Calendar, Cookies, Restaurant, SeatingData } from '@maverick/entity';
import { Icon } from '@maverick/ui';
import { filterRestaurantHours, GaEvent, getDateWithOffset, handleRestaurantInfo } from '@maverick/utils';
import { FC, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { IconClock } from '@maverick/icons/dist/IconClock';
import * as Styled from './LocationPopUpContent.styled';
import Link from 'next/link';
import { useCookies } from '@maverick/hooks';
import { getConceptFromExtRef, LocationManager } from '@maverick/cms';
import { LocationPopUpContentReduxProps } from '.';

interface LocationPopUpContentProps extends LocationPopUpContentReduxProps {
    seatingDetails: string | null;
    seatingData: SeatingData | null;
    handleModal: () => void;
}

export const LocationPopUpContent: FC<LocationPopUpContentProps> = ({
    seatingDetails,
    seatingData,
    handleModal,
    waitTime,
    restaurant,
    setWaitTime,
    setEzCaterUrl,
    setUserTriggeredLocationChange,
}) => {
    const { cmsComponents } = useTheme();
    const [favoriteStore] = useCookies(Cookies.FavoriteStore);
    const [status, setStatus] = useState<boolean | null>(null);
    const [restaurantInfo, setRestaurantInfo] = useState({
        restaurantName: null as string | null,
        restaurantAddress: null as string | null,
        restaurantHours: null as string | null | undefined,
    });
    const [yextUrl, setYextUrl] = useState<string | null>(null);

    useEffect(() => {
        handleRestaurantHours();
        handleLocation();
    }, [favoriteStore]);

    const handleLocation = async () => {
        await Promise.all([handleRestaurantOpen(), handleTableWait(), handleYextUrl(), handleEzCaterUrl()]);
    };

    const handleRestaurantOpen = async () => {
        if (!favoriteStore || !restaurant) {
            return;
        }
        const result = await LocationManager.GetLocationCalendars(restaurant.id);
        if (!result) {
            return;
        }

        const now = getDateWithOffset(restaurant.utcoffset);
        const isOpen = result.businessCalendar?.ranges.some((r) => now >= r.start && now <= r.end) ?? null;
        const calendar = () => {
            const calendarArray = [result.businessCalendar, result.curbsideCalendar, result.deliveryCalendar];
            const resultArray: Calendar[] = [];
            calendarArray.forEach((c) => {
                if (!!c) resultArray.push(c);
            });
            return resultArray.length > 0 ? resultArray : undefined;
        };

        const newRestaurant = { ...restaurant, calendars: calendar() };
        handleRestaurantHours(newRestaurant);
        setStatus(isOpen);
    };

    const handleRestaurantHours = (newRestaurant?: Restaurant) => {
        const selectedRestaurant = newRestaurant || restaurant;
        if (!selectedRestaurant) {
            return;
        }
        const { restaurantName, restaurantAddress } = handleRestaurantInfo(selectedRestaurant);
        const restaurantHours =
            selectedRestaurant.calendars && selectedRestaurant.calendars.length > 0
                ? filterRestaurantHours(selectedRestaurant.calendars)
                : null;
        setRestaurantInfo({
            restaurantName,
            restaurantAddress,
            restaurantHours,
        });
    };

    const handleTableWait = async () => {
        if (!favoriteStore || !restaurant) {
            return;
        }

        const result = await LocationManager.GetTableWait(favoriteStore, getConceptFromExtRef(restaurant));
        if (!result) {
            setWaitTime(null);
            return;
        }

        setWaitTime({ time: result.waitTime });
    };
    const renderRestaurantHours = () => {
        return (
            <Styled.RestaurantHoursContainer>
                <Styled.HoursIconContainer>
                    <Icon icon={IconClock} customSize />
                </Styled.HoursIconContainer>
                <Styled.RestaurantHours
                    data-testid='location-restaurant-hours'
                    aria-label={
                        restaurantInfo.restaurantHours
                            ? `Restaurant hours: ${restaurantInfo.restaurantHours}`
                            : undefined
                    }
                    aria-hidden={restaurantInfo.restaurantHours === null}
                >
                    Restaurant hours: {restaurantInfo.restaurantHours}
                </Styled.RestaurantHours>
            </Styled.RestaurantHoursContainer>
        );
    };

    const waitTimeEvent = () => {
        if (status && waitTime !== null && waitTime.time.toString() === '0') {
            GaEvent.TopNavigation('No wait');
        }
        GaEvent.InternalLinkClick('/seating');
        handleModal();
    };

    const renderSeatingDetails = () => {
        if (seatingDetails && seatingData) {
            return (
                <Styled.SeatingContainer>
                    <Styled.SeatingDetails data-testid='location-seating-details' aria-label={`${seatingDetails}`}>
                        <Link
                            href='/seating/details'
                            onClick={() => {
                                GaEvent.TopNavigation('details');
                                GaEvent.InternalLinkClick('/seating/details');
                            }}
                        >
                            {seatingDetails}
                        </Link>
                    </Styled.SeatingDetails>
                </Styled.SeatingContainer>
            );
        }

        if (status !== null && waitTime !== null) {
            return (
                <Styled.SeatingContainer>
                    <Styled.SeatingStatus
                        data-testid='location-seating-status'
                        status={status}
                        aria-label={`${status}`}
                    >
                        {status ? 'Open' : 'Closed'}
                    </Styled.SeatingStatus>
                    <Styled.SeatingDetails data-testid='location-wait-time'>
                        &nbsp;/&nbsp;
                        <Link href='/seating' onClick={() => waitTimeEvent()}>
                            {status && waitTime.time.toString() !== '0' ? `${waitTime.time} min. wait` : 'No wait time'}
                        </Link>
                    </Styled.SeatingDetails>
                </Styled.SeatingContainer>
            );
        }

        return null;
    };

    const handleYextUrl = async () => {
        if (!favoriteStore) {
            setYextUrl(null);
            return;
        }

        const result = await LocationManager.GetLocationInfo(favoriteStore);
        if (!result) {
            setYextUrl(null);
            return;
        }

        setYextUrl(result.yextUrl || null);
    };

    const renderLinks = () => {
        if (yextUrl === null) return null;

        return (
            <Styled.LinksContainer>
                <Styled.StyledLink
                    data-testid='change-location'
                    id='change-location'
                    onClick={() => {
                        setUserTriggeredLocationChange(true);
                        GaEvent.TopNavigation('Change location');
                    }}
                    href='https://locations.outback.com/search'
                    variant='change-location'
                >
                    Change location
                </Styled.StyledLink>
                <Styled.StyledLink
                    data-testid='view-more'
                    id='view-more'
                    onClick={() => GaEvent.TopNavigation('View info')}
                    href={yextUrl ? yextUrl : 'https://locations.outback.com/search'}
                    variant='view-more-info'
                >
                    View more info
                </Styled.StyledLink>
            </Styled.LinksContainer>
        );
    };

    const handleEzCaterUrl = async () => {
        if (!favoriteStore) {
            setEzCaterUrl(null);
            return;
        }

        const result = await LocationManager.GetEzCaterUrl(favoriteStore);

        if (!result) {
            setEzCaterUrl(null);
            return;
        }

        setEzCaterUrl(result);
    };

    return (
        <Styled.Container data-testid='location-pop-up'>
            <Styled.Header id='location-header' data-testid='location-brand-name'>
                Your {cmsComponents.shortName}
            </Styled.Header>
            <Styled.RestaurantNameContainer>
                <Styled.NameIconContainer>
                    <Icon icon={cmsComponents.icons.iconLocation} customSize />
                </Styled.NameIconContainer>
                <Styled.RestaurantName
                    data-testid='location-restaurant-name'
                    aria-label={
                        restaurantInfo.restaurantName ? `Restaurant name: ${restaurantInfo.restaurantName}` : undefined
                    }
                >
                    {restaurantInfo.restaurantName}
                </Styled.RestaurantName>
            </Styled.RestaurantNameContainer>
            <Styled.RestaurantAddress
                data-testid='location-restaurant-address'
                aria-label={
                    restaurantInfo.restaurantAddress
                        ? `Restaurant address: ${restaurantInfo.restaurantAddress}`
                        : undefined
                }
            >
                {restaurantInfo.restaurantAddress}
            </Styled.RestaurantAddress>
            {renderRestaurantHours()}
            {renderSeatingDetails()}
            {renderLinks()}
        </Styled.Container>
    );
};
